import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { profileService, DoctorProfile } from '../../../services/profile/profileService';
import { authService } from '../../../services/auth/authService';
import { apiClient } from '../../../services/api/apiClient';
import { UserMenu } from '../UserMenu/UserMenu';
import '../../../pages/dashboard/Dashboard.css';

interface DashboardHeaderProps {
  onProfileClick?: () => void;
  onBillingClick?: () => void;
  onLogout?: () => void;
}

export const DashboardHeader: React.FC<DashboardHeaderProps> = ({
  onProfileClick,
  onBillingClick,
  onLogout,
}) => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<DoctorProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [avatarBlobUrl, setAvatarBlobUrl] = useState<string | null>(null);
  const avatarBlobUrlRef = useRef<string | null>(null);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const profileData = await profileService.getProfile();
        setProfile(profileData);
      } catch (error) {
        console.error('Failed to load profile:', error);
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, []);

  // Fetch avatar with auth so it loads in <img> (direct avatar URL may not send cookies/Authorization cross-origin)
  useEffect(() => {
    const avatarUrl = profile?.avatar_url?.trim();
    if (!avatarUrl) {
      if (avatarBlobUrlRef.current) {
        URL.revokeObjectURL(avatarBlobUrlRef.current);
        avatarBlobUrlRef.current = null;
      }
      setAvatarBlobUrl(null);
      return;
    }
    let cancelled = false;
    if (avatarBlobUrlRef.current) {
      URL.revokeObjectURL(avatarBlobUrlRef.current);
      avatarBlobUrlRef.current = null;
    }
    apiClient
      .get(avatarUrl, { responseType: 'blob' })
      .then((res) => {
        if (cancelled || !res.data) return;
        const url = URL.createObjectURL(res.data);
        avatarBlobUrlRef.current = url;
        setAvatarBlobUrl(url);
      })
      .catch(() => {
        if (!cancelled) setAvatarBlobUrl(null);
      });
    return () => {
      cancelled = true;
      if (avatarBlobUrlRef.current) {
        URL.revokeObjectURL(avatarBlobUrlRef.current);
        avatarBlobUrlRef.current = null;
      }
    };
  }, [profile?.avatar_url]);

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    } else {
      authService.clearTokens();
      navigate('/login');
    }
  };

  const handleGoToProfile = () => {
    if (onProfileClick) {
      onProfileClick();
    } else {
      navigate('/profile');
    }
  };

  const handleGoToBilling = () => {
    if (onBillingClick) {
      onBillingClick();
    } else {
      navigate('/billing');
    }
  };

  const getDisplayName = () => {
    if (profile?.full_name) return profile.full_name;
    if (profile?.first_name || profile?.last_name) {
      return [profile.first_name, profile.last_name].filter(Boolean).join(' ');
    }
    return 'Doctor';
  };

  const displayName = getDisplayName();
  const specialization = profile?.specialization_name || null;
  const profilePhotoUrl = avatarBlobUrl;

  return (
    <header className="dashboard-header">
      <div className="dashboard-header-left">
        <button
          type="button"
          className="app-logo"
          onClick={() => navigate('/dashboard')}
        >
          <img src="/images/zivo_logo.png" alt="ZivoHealth" />
        </button>
      </div>
      <div className="dashboard-header-right">
        {!loading && (
          <UserMenu
            displayName={`Dr. ${displayName}`}
            specialization={specialization}
            profilePhotoUrl={profilePhotoUrl}
            onProfileClick={handleGoToProfile}
            onBillingClick={handleGoToBilling}
            onLogout={handleLogout}
          />
        )}
      </div>
    </header>
  );
};

